import {z} from 'zod'

import {tzDateFromIso} from '../api/zod-helpers'

const transportationRatePolicyRawSchema = z.object({
    id: z.string(),
    minimum_hourly_rate: z.string(),
    default_hourly_rate: z.string().nullable(),
    created_at: z.string(),
    created_by_id: z.string().nullable(),
})

export const transportationRatePolicySchema = transportationRatePolicyRawSchema.transform(
    (raw) => ({
        id: raw.id,
        minimumHourlyRate: Number(raw.minimum_hourly_rate),
        defaultHourlyRate: raw.default_hourly_rate == null ? null : Number(raw.default_hourly_rate),
        createdAt: tzDateFromIso.parse(raw.created_at),
        createdById: raw.created_by_id,
    }),
)

export type TransportationRatePolicyRaw = z.infer<typeof transportationRatePolicyRawSchema>
export type TransportationRatePolicy = z.infer<typeof transportationRatePolicySchema>

export const createTransportationRatePolicyInputSchema = z.object({
    minimumHourlyRate: z.number().min(0),
    defaultHourlyRate: z.number().min(0).nullable(),
})

export type CreateTransportationRatePolicyInput = z.input<
    typeof createTransportationRatePolicyInputSchema
>

export const createTransportationRatePolicyRequestSchema =
    createTransportationRatePolicyInputSchema.transform(
        ({minimumHourlyRate, defaultHourlyRate}) => ({
            minimum_hourly_rate: minimumHourlyRate.toFixed(2),
            default_hourly_rate: defaultHourlyRate == null ? null : defaultHourlyRate.toFixed(2),
        }),
    )

export type CreateTransportationRatePolicyRequestRaw = z.infer<
    typeof createTransportationRatePolicyRequestSchema
>

const serviceTypeRawSchema = z.object({
    id: z.string(),
    name: z.string(),
    active: z.boolean(),
    specific_types: z.array(z.string()),
    is_technician_work: z.boolean().default(false),
})

export const serviceTypeSchema = serviceTypeRawSchema.transform((raw) => ({
    id: raw.id,
    name: raw.name,
    active: raw.active,
    specificTypes: raw.specific_types,
    isTechnicianWork: raw.is_technician_work,
}))

export type ServiceTypeRaw = z.input<typeof serviceTypeRawSchema>
export type ServiceType = z.infer<typeof serviceTypeSchema>

export const createServiceTypeInputSchema = z.object({
    name: z.string().transform((value) => value.trim()),
})

export type CreateServiceTypeInput = z.input<typeof createServiceTypeInputSchema>

export const createServiceTypeRequestSchema = createServiceTypeInputSchema.transform(({name}) => ({
    name,
}))

export type CreateServiceTypeRequestRaw = z.infer<typeof createServiceTypeRequestSchema>

export const updateServiceTypeInputSchema = z.object({
    name: z
        .string()
        .transform((value) => value.trim())
        .optional(),
    active: z.boolean().optional(),
    specificTypes: z.array(z.string()).optional(),
    isTechnicianWork: z.boolean().optional(),
})

export type UpdateServiceTypeInput = z.input<typeof updateServiceTypeInputSchema>

export const updateServiceTypeRequestSchema = updateServiceTypeInputSchema.transform(
    ({name, active, specificTypes, isTechnicianWork}) => ({
        ...(name !== undefined ? {name} : {}),
        ...(active !== undefined ? {active} : {}),
        ...(specificTypes !== undefined ? {specific_types: specificTypes} : {}),
        ...(isTechnicianWork !== undefined ? {is_technician_work: isTechnicianWork} : {}),
    }),
)

export type UpdateServiceTypeRequestRaw = z.infer<typeof updateServiceTypeRequestSchema>
